export const MAX_ATTACHMENT_SIZE_MB = 100;

export const ACCEPTED_ATTACHMENT_EXTENSIONS = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv', '.txt', '.rtf', '.md',
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tif', '.tiff',
    '.zip', '.rar', '.7z', '.tar', '.gz',
    '.mp4', '.mov', '.avi', '.mkv', '.webm', '.mp3', '.wav', '.m4a',
    '.json', '.xml', '.yaml', '.yml', '.fig', '.sketch', '.dwg', '.psd', '.ai', '.eps',
];

const BLOCKED_ATTACHMENT_EXTENSIONS = new Set([
    '.ade', '.adp', '.apk', '.app', '.asp', '.aspx', '.bat', '.bin', '.cmd',
    '.com', '.cpl', '.dll', '.dmg', '.exe', '.gadget', '.hta', '.ins', '.ipa',
    '.iso', '.jar', '.js', '.jse', '.jsp', '.ksh', '.lnk', '.msi', '.msp',
    '.pif', '.ps1', '.scr', '.sh', '.sys', '.vb', '.vbe', '.vbs', '.ws',
    '.wsc', '.wsf', '.wsh',
]);

export const ATTACHMENT_ACCEPT = ACCEPTED_ATTACHMENT_EXTENSIONS.join(',');
export const SUPPORTED_ATTACHMENT_HELP_TEXT = 'Supported: documents, spreadsheets, presentations, images, archives, media, design and data files.';
export const BLOCKED_ATTACHMENT_HELP_TEXT = 'Blocked for security: apps, executables, installers, scripts, shortcuts and system files.';

export function getFileExtension(name = '') {
    const index = name.lastIndexOf('.');
    return index >= 0 ? name.slice(index).toLowerCase() : '';
}

export function validateAttachmentFile(file) {
    const ext = getFileExtension(file.name);
    if (BLOCKED_ATTACHMENT_EXTENSIONS.has(ext)) {
        return { valid: false, reason: 'This file type is blocked for security. Executables, installers and scripts are not allowed.' };
    }
    if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
        return { valid: false, reason: 'File is too large. Maximum size is ' + MAX_ATTACHMENT_SIZE_MB + 'MB.' };
    }
    return { valid: true };
}

export function isImageFile(fileOrAttachment) {
    if (!fileOrAttachment) return false;
    const mime = (fileOrAttachment?.type || fileOrAttachment?.mimeType || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    const name = (fileOrAttachment?.name || fileOrAttachment?.originalName || fileOrAttachment?.fileName || '').toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name);
}

export function isQuillEmpty(content) {
    if (!content || typeof content !== 'string') return true;
    const stripped = content.replace(/<[^>]*>/g, '').trim();
    const hasMedia = /<img|<iframe|<video/i.test(content);
    return stripped.length === 0 && !hasMedia;
}

export function formatBytes(bytes = 0) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    const precision = unitIndex === 0 ? 0 : 1;
    return size.toFixed(precision) + ' ' + units[unitIndex];
}

// In-memory cache for resolved R2 attachment URLs & optimistic preview URLs
const optimisticImageUrlCache = new Map();
const GENERIC_NAMES = new Set(['image.png', 'image.jpg', 'image.jpeg', 'screenshot.png', 'file', 'image']);

export function setOptimisticImageUrl(key, url) {
    if (!key || !url) return;
    const str = String(key).toLowerCase().trim();
    if (GENERIC_NAMES.has(str)) return;
    optimisticImageUrlCache.set(str, url);
}

export function getOptimisticImageUrl(key) {
    if (!key) return null;
    const str = String(key).toLowerCase().trim();
    if (GENERIC_NAMES.has(str)) return null;
    return optimisticImageUrlCache.get(str) || null;
}

export function deleteOptimisticImageUrl(key) {
    if (!key) return;
    optimisticImageUrlCache.delete(String(key).toLowerCase().trim());
}

export function clearOptimisticImageUrlCache() {
    optimisticImageUrlCache.clear();
}

export function createPendingAttachment(file) {
    const previewUrl = isImageFile(file) ? URL.createObjectURL(file) : null;
    const pendingId = file.name + '-' + file.lastModified + '-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    if (previewUrl) {
        setOptimisticImageUrl(pendingId, previewUrl);
        if (file?.name) setOptimisticImageUrl(file.name, previewUrl);
    }
    return {
        id: pendingId,
        file,
        aiConfig: file.aiConfig || null,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
        status: 'pending',
        progress: 0,
        error: '',
    };
}

export function cleanupPendingAttachment(entry) {
    if (entry?.abortController) {
        try {
            entry.abortController.abort();
        } catch { /* ignore */ }
    }
    if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    if (entry?.id) deleteOptimisticImageUrl(entry.id);
    if (entry?.name) deleteOptimisticImageUrl(entry.name);
}

export async function downloadFileBlob(url, filename = 'download') {
    if (!url) return;
    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error('Network response not ok: ' + response.status);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            try { document.body.removeChild(a); } catch {}
            window.URL.revokeObjectURL(blobUrl);
        }, 300);
    } catch {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            try { document.body.removeChild(a); } catch {}
        }, 500);
    }
}
